import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CalendarMonthOutlined,
  CurrencyRupeeOutlined,
  ForumOutlined,
  TodayOutlined,
} from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";

import useAuthUser from "../hooks/useAuthUser";
import { ConversationBillingSummary } from "../../models/conversationBilling.model";
import { getConversationBillingSummary } from "../../services/conversationBilling.service";
import { getUserSettingsById } from "../../services/settings.service";

const RANGE_OPTIONS = [7, 30, 90];

export function formatPaise(amountPaise: number, currency = "INR") {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format((amountPaise || 0) / 100);
}

function MetricCard({
  label,
  count,
  amountPaise,
  currency,
  icon,
}: {
  label: string;
  count: number;
  amountPaise: number;
  currency: string;
  icon: React.ReactNode;
}) {
  return (
    <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {label}
          </Typography>
          <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
        </Stack>
        <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
          {count.toLocaleString("en-IN")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {formatPaise(amountPaise, currency)} billed
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function ConversationBillingDashboard(
  { businessId: selectedBusinessId }: { businessId?: string } = {},
) {
  const { user } = useAuthUser();
  const theme = useTheme();
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<ConversationBillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setError(null);
    try {
      const settings = selectedBusinessId ? null : await getUserSettingsById(user.uid);
      const businessId = selectedBusinessId || settings?.businessSettings?.businessId;
      if (!businessId) {
        setSummary(null);
        setError("Connect a WhatsApp business account to begin tracking conversations.");
        return;
      }
      setSummary(await getConversationBillingSummary(businessId, days));
    } catch (loadError: any) {
      setError(
        loadError?.response?.data?.detail ||
          "Conversation usage could not be loaded. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [days, selectedBusinessId, user?.uid]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const chartData = useMemo(
    () =>
      (summary?.daily || []).map((row) => ({
        ...row,
        label: dayjs(row.date).format(days <= 7 ? "ddd" : "DD MMM"),
      })),
    [days, summary?.daily],
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: 280, display: "grid", placeItems: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={700}>
              Conversation usage & billing
            </Typography>
            {summary && (
              <Tooltip
                title={`One conversation covers AI replies to the same customer for ${summary.conversationWindowHours} hours.`}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${summary.conversationWindowHours}-hour window`}
                />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Billable AI conversations, grouped in your business timezone.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="usage-range-label">Chart range</InputLabel>
          <Select
            labelId="usage-range-label"
            value={days}
            label="Chart range"
            onChange={(event) => setDays(Number(event.target.value))}
          >
            {RANGE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                Last {option} days
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert severity="info">{error}</Alert>}

      {summary && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            <MetricCard
              label="Today"
              {...summary.today}
              currency={summary.currency}
              icon={<TodayOutlined />}
            />
            <MetricCard
              label="This week"
              {...summary.thisWeek}
              currency={summary.currency}
              icon={<ForumOutlined />}
            />
            <MetricCard
              label="This month"
              {...summary.thisMonth}
              currency={summary.currency}
              icon={<CalendarMonthOutlined />}
            />
            <MetricCard
              label={`Last ${summary.rangeDays} days`}
              {...summary.range}
              currency={summary.currency}
              icon={<CurrencyRupeeOutlined />}
            />
          </Box>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Conversations by day
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Daily total for the selected range · {summary.timezone}
                  </Typography>
                </Box>
                <Chip
                  color="primary"
                  variant="outlined"
                  label={`${formatPaise(summary.currentRatePaise, summary.currency)} / conversation`}
                />
              </Stack>
              <Box sx={{ width: "100%", height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={18} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                    <ChartTooltip
                      formatter={(value) => [
                        Number(value || 0).toLocaleString("en-IN"),
                        "Conversations",
                      ]}
                      labelFormatter={(_, payload) =>
                        payload?.[0]?.payload?.date
                          ? dayjs(payload[0].payload.date).format("DD MMMM YYYY")
                          : ""
                      }
                    />
                    <Bar dataKey="count" name="Conversations" fill={theme.palette.primary.main} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}
